import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaUsers, FaLaptop, FaMobileAlt, FaTabletAlt, FaUserCog, FaSitemap, FaSearch, FaSync,
  FaSignInAlt, FaUserCheck, FaQrcode, FaExclamationTriangle, FaChevronRight,
} from 'react-icons/fa';
import Sidebar from './Sidebar';
import Topbar from './Topbar';
import UserAvatar from './UserAvatar';
import UserActivityPanel from './UserActivityPanel';
import { API_BASE, authFetch } from '../utils/api';
import { ADMIN_ROLES, hasRole } from '../config/roles';
import { minutesSince, relativeTime } from '../utils/time';

/**
 * Shared body of /session-management and /device-management.
 *
 * Open to every signed-in member: a narrow member list (tap someone for their
 * full detail) beside a live "latest activity" feed of logins, installation
 * verifications and check-ins. Refreshes itself every 20 s while the tab is
 * visible. Admins additionally see IPs and the manage actions -- those are
 * enforced by the backend, not just hidden here.
 */

const POLL_MS = 20000;
const ONLINE_WITHIN_MIN = 5;

const FEED_ICON = {
  login:   { icon: FaSignInAlt, color: 'var(--info)' },
  verify:  { icon: FaUserCheck, color: 'var(--success)' },
  checkin: { icon: FaQrcode,    color: 'var(--warning)' },
};

const COPY = {
  sessions: {
    title: 'Session Management',
    desc: 'Who is signed in right now, and when everyone last logged in.',
    listTitle: 'Members',
    defaultTab: 'activity',
  },
  devices: {
    title: 'Device Management',
    desc: 'The devices members sign in from. Tap a member for their full history.',
    listTitle: 'Members & devices',
    defaultTab: 'devices',
  },
};

function DeviceGlyph({ type, size = 12 }) {
  if (type === 'Tablet') return <FaTabletAlt size={size} />;
  if (type === 'Mobile') return <FaMobileAlt size={size} />;
  return <FaLaptop size={size} />;
}

export default function MemberMonitor({ mode = 'sessions' }) {
  const copy = COPY[mode] || COPY.sessions;

  const [sessions, setSessions] = useState([]);
  const [recent, setRecent]     = useState([]);
  const [isAdmin, setIsAdmin]   = useState(false);
  const [skew, setSkew]         = useState(0);       // server clock - client clock, ms
  const [loading, setLoading]   = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]       = useState(null);
  const [stale, setStale]       = useState(false);    // a background refresh failed
  const [query, setQuery]       = useState('');
  const [filter, setFilter]     = useState('all');    // all | online | signed
  const [tab, setTab]           = useState('members'); // phone: members | latest
  const [openMember, setOpenMember] = useState(null);

  const user = useMemo(() => {
    try { return JSON.parse(localStorage.getItem('user')) || null; } catch { return null; }
  }, []);
  const viewerIsAdmin = hasRole(user?.role, ADMIN_ROLES);
  const topbarUser = user || { name: 'Unknown User', role: 'Member', image: '/user.png' };

  const load = useCallback(async ({ silent = false } = {}) => {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/getSessionData.php`);
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.success) {
        throw new Error(data?.error || `Request failed (${res.status})`);
      }
      setSessions(data.sessions || []);
      setRecent(data.recent || []);
      setIsAdmin(!!data.isAdmin);
      if (data.serverTime) {
        const t = new Date(data.serverTime).getTime();
        if (!Number.isNaN(t)) setSkew(t - Date.now());
      }
      setError(null);
      setStale(false);
    } catch (err) {
      // A failed background refresh keeps the last good data on screen.
      if (silent) setStale(true);
      else setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    load();
    const tick = () => { if (!document.hidden) load({ silent: true }); };
    const id = setInterval(tick, POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [load]);

  const nowMs = Date.now() + skew;

  const statusOf = useCallback((s) => {
    if (!(s.activeSessions > 0)) return 'offline';
    return minutesSince(s.lastSeen, Date.now() + skew) <= ONLINE_WITHIN_MIN ? 'online' : 'signed';
  }, [skew]);

  const counts = useMemo(() => {
    let online = 0; let signed = 0;
    sessions.forEach((s) => {
      const st = statusOf(s);
      if (st === 'online') { online += 1; signed += 1; } else if (st === 'signed') signed += 1;
    });
    return { online, signed };
  }, [sessions, statusOf]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return sessions.filter((s) => {
      const st = statusOf(s);
      if (filter === 'online' && st !== 'online') return false;
      if (filter === 'signed' && st === 'offline') return false;
      if (!q) return true;
      return [s.name, s.sliitId, s.role, s.device, s.ipAddress]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [sessions, query, filter, statusOf]);

  const StatusPill = ({ s }) => {
    const st = statusOf(s);
    if (st === 'online') {
      return <span className="pill pill-success"><span className="dot" />Online</span>;
    }
    if (st === 'signed') {
      return <span className="pill pill-warning"><span className="dot" />Signed in</span>;
    }
    return null;
  };

  return (
    <>
      <Sidebar />
      <Topbar user={topbarUser} />

      <main className="lg:ml-64 mt-14 ml-0 px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        <div className="page-head">
          <div>
            <h1>{copy.title}</h1>
            <p className="page-desc">{copy.desc}</p>
          </div>
        </div>

        <div className="filter-row mon-nav" style={{ flexWrap: 'wrap', marginBottom: 'var(--sp-5)' }}>
          {viewerIsAdmin && (
            <Link to="/admin" className="chip"><FaUserCog size={12} /> Role Management</Link>
          )}
          <Link to="/session-management" className={`chip ${mode === 'sessions' ? 'active' : ''}`}>
            <FaUsers size={12} /> Session Management
          </Link>
          <Link to="/device-management" className={`chip ${mode === 'devices' ? 'active' : ''}`}>
            <FaLaptop size={12} /> Device Management
          </Link>
          {viewerIsAdmin && (
            <Link to="/path-management" className="chip"><FaSitemap size={12} /> Role Path Management</Link>
          )}
        </div>

        {/* Phone: pick one of the two columns */}
        <div className="mon-tabs" role="tablist">
          <button role="tab" aria-selected={tab === 'members'} className={`chip ${tab === 'members' ? 'active' : ''}`} onClick={() => setTab('members')}>
            Members <span className="num">({sessions.length})</span>
          </button>
          <button role="tab" aria-selected={tab === 'latest'} className={`chip ${tab === 'latest' ? 'active' : ''}`} onClick={() => setTab('latest')}>
            Latest activity
          </button>
        </div>

        {error ? (
          <section className="panel" style={{ padding: 'var(--sp-4)' }}>
            <div className="error-banner">
              <FaExclamationTriangle style={{ marginTop: 2, flexShrink: 0 }} />
              <span>{error}</span>
            </div>
            <button onClick={() => load()} className="btn btn-primary btn-sm" style={{ marginTop: 'var(--sp-3)' }}>
              Try again
            </button>
          </section>
        ) : (
          <div className="mon-grid">
            {/* ---------------- Member list (narrow) ---------------- */}
            <section className={`panel mon-col ${tab === 'members' ? 'is-on' : ''}`}>
              <div className="mon-head">
                <div className="mon-title">
                  <h2>{copy.listTitle}</h2>
                  <span className="live-dot" title={stale ? 'Live updates paused -- retrying' : `Updates every ${POLL_MS / 1000}s`}
                        style={stale ? { color: 'var(--warning)' } : undefined}>
                    <span className="live-dot-pulse" style={stale ? { background: 'var(--warning)' } : undefined} />
                    {stale ? 'RETRYING' : 'LIVE'}
                  </span>
                  <button className="icon-btn" style={{ marginLeft: 'auto' }} onClick={() => load({ silent: true })}
                          disabled={loading || refreshing} aria-label="Refresh" title="Refresh">
                    {refreshing ? <span className="spinner" /> : <FaSync size={12} />}
                  </button>
                </div>

                <div className="mon-search">
                  <FaSearch size={12} />
                  <input type="search" aria-label="Search members" placeholder="Search name, ID, role…"
                         value={query} onChange={(e) => setQuery(e.target.value)} />
                </div>

                <div className="mon-filters" role="group" aria-label="Filter members">
                  <button className={`chip ${filter === 'all' ? 'active' : ''}`} onClick={() => setFilter('all')}>
                    All <span className="num">{sessions.length}</span>
                  </button>
                  <button className={`chip ${filter === 'online' ? 'active' : ''}`} onClick={() => setFilter('online')}>
                    Online <span className="num">{counts.online}</span>
                  </button>
                  <button className={`chip ${filter === 'signed' ? 'active' : ''}`} onClick={() => setFilter('signed')}>
                    Signed in <span className="num">{counts.signed}</span>
                  </button>
                </div>
              </div>

              <div className="mon-scroll">
                {loading ? (
                  <div className="empty-state"><span className="spinner lg" /><p>Loading members…</p></div>
                ) : filtered.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><FaUsers /></div>
                    <h3>{sessions.length === 0 ? 'No logins yet' : 'No members match'}</h3>
                    <p>{sessions.length === 0 ? 'Members appear here once they sign in.' : 'Try a different search or filter.'}</p>
                    {sessions.length > 0 && (
                      <button className="btn btn-ghost btn-sm" onClick={() => { setQuery(''); setFilter('all'); }}>Clear filters</button>
                    )}
                  </div>
                ) : (
                  <ul className="mon-list">
                    {filtered.map((s) => {
                      const st = statusOf(s);
                      return (
                        <li key={s.id}>
                          <button type="button" className="mon-row" onClick={() => setOpenMember(s.sliitId)}
                                  aria-label={`Open details for ${s.name}`}>
                            <span className="mon-avatar">
                              <UserAvatar src={s.image} alt="" size={40} ring="var(--surface-1)" fallback="/user.png" />
                              <i className={`mon-dot ${st}`} aria-hidden="true" />
                            </span>
                            <span className="mon-main">
                              <span className="mon-name truncate">{s.name}</span>
                              <span className="mon-sub">
                                {mode === 'devices' ? (
                                  <>
                                    <DeviceGlyph type={s.deviceType} />
                                    <span className="truncate">{s.device}{isAdmin && s.ipAddress ? ` · ${s.ipAddress}` : ''}</span>
                                  </>
                                ) : (
                                  <span className="truncate">
                                    {s.role}{s.accountBlock ? ' · Restricted' : ''}
                                    {' · '}
                                    {st === 'offline'
                                      ? (s.lastLoginISO ? `last login ${relativeTime(s.lastLoginISO, nowMs)}` : 'never signed in')
                                      : `seen ${relativeTime(s.lastSeen, nowMs)}`}
                                  </span>
                                )}
                              </span>
                            </span>
                            <span className="mon-side">
                              {st === 'offline'
                                ? <span className="mon-off">Offline</span>
                                : <StatusPill s={s} />}
                              <span className={`mon-count ${s.activeSessions > 0 ? 'has' : ''}`}
                                    title="Sessions signed in right now">
                                <b className="num">{s.activeSessions}</b> {s.activeSessions === 1 ? 'session' : 'sessions'}
                              </span>
                            </span>
                            <FaChevronRight size={10} className="mon-chev" />
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>

            {/* ---------------- Latest activity ---------------- */}
            <section className={`panel mon-col ${tab === 'latest' ? 'is-on' : ''}`}>
              <div className="mon-head">
                <div className="mon-title">
                  <h2>Latest activity</h2>
                  <span className="chip" style={{ cursor: 'default' }}>Logins · Installation</span>
                </div>
              </div>

              <div className="mon-scroll">
                {loading ? (
                  <div className="empty-state"><span className="spinner lg" /><p>Loading activity…</p></div>
                ) : recent.length === 0 ? (
                  <div className="empty-state">
                    <div className="empty-icon"><FaSignInAlt /></div>
                    <h3>Nothing yet</h3>
                    <p>Logins, verifications and check-ins show up here as they happen.</p>
                  </div>
                ) : (
                  <ul className="mon-feed">
                    {recent.map((r, i) => {
                      const meta = FEED_ICON[r.type] || FEED_ICON.login;
                      const Icon = meta.icon;
                      return (
                        <li key={`${r.type}-${r.actorId}-${r.at}-${i}`}>
                          <button type="button" className="mon-feed-row" onClick={() => setOpenMember(r.actorId)}
                                  aria-label={`Open details for ${r.actorName}`}>
                            <span className="mon-avatar">
                              <UserAvatar src={r.actorImage} alt="" size={34} ring="var(--surface-1)" fallback="/user.png" />
                              <i className="mon-badge" style={{ background: meta.color }}><Icon size={8} /></i>
                            </span>
                            <span className="mon-main">
                              <span className="mon-name truncate"><b>{r.actorName}</b></span>
                              <span className="mon-sub truncate">{r.text}{r.detail ? ` · ${r.detail}` : ''}</span>
                            </span>
                            <span className="mon-when">{relativeTime(r.at, nowMs) || '—'}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </section>
          </div>
        )}
      </main>

      {openMember && (
        <UserActivityPanel
          sliitId={openMember}
          initialTab={copy.defaultTab}
          onClose={() => setOpenMember(null)}
          onChanged={() => load({ silent: true })}
        />
      )}
    </>
  );
}
